import { useMutation } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DocumentDialogContainer } from "components/Document/shared";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { GetShippingDocumentQuery, UnitType, WasteKind } from "generated-graphql/graphql";
import { Dispatch, SetStateAction, useState } from "react";
import {
  Control,
  useFieldArray,
  useForm,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { FACILITY_WASTE_STREAM_OPTIONS_QUERY } from "routes/Customer/Waste/Containers/api";
import { UNIT_LABELS } from "routes/Customer/Waste/Containers/containerDisplay";
import { SHIPPING_PAPERWORK_QUERY } from "routes/Customer/Waste/Manifests/api";
import { SHIPPABLE_CONTAINERS_QUERY } from "routes/Customer/Waste/Shipments/api";

import {
  GET_SHIPPING_DOCUMENT_QUERY,
  SAVE_WASTE_SHIPPING_DOCUMENT,
  SHIPPING_DOCUMENT_LINES_QUERY,
} from "./api";
import { DECLARED_WASTE_KIND_LABELS } from "./extractionReview";
import { SHIPPING_DOCUMENT_KIND_OPTIONS } from "./shippingDocumentDisplay";
import {
  EMPTY_LINE,
  ShippingDocumentFormValues,
  toFormValues,
  toSaveInput,
} from "./shippingDocumentForm";

const HALF = { xs: 12, sm: 6 };

/*
 * A line's fields over two rows of three rather than one row of six.
 *
 * Six across assumed the form had the whole dialog. Beside the document preview
 * the form column is a little over half of it, which left each field about
 * 115px wide -- a description showing one word and a unit showing "P...". Three
 * per row is legible in the narrow column and still reads as one line's worth
 * of fields in the wide one.
 */
const LINE_DESCRIPTION = { xs: 12, sm: 5 };
const LINE_QUANTITY = { xs: 6, sm: 3 };
const LINE_UNIT = { xs: 6, sm: 4 };
const LINE_THIRD = { xs: 12, sm: 4 };

/** Swallows a close attempt while a save is in flight. */
const noop = () => {};

const UNIT_OPTIONS = Object.values(UnitType).map((value) => ({ value, label: UNIT_LABELS[value] }));
const DECLARED_WASTE_KIND_OPTIONS = [
  { value: null, display: "—" },
  ...Object.values(WasteKind).map((value) => ({
    value,
    display: DECLARED_WASTE_KIND_LABELS[value],
  })),
];

type Document = NonNullable<GetShippingDocumentQuery["wasteShippingDocument"]>;
type FormControl = Control<ShippingDocumentFormValues>;

type StreamPickerOption = { id: string; name: string };
type ContainerPickerOption = { id: string; containerLabel: string };

/** One line's stream and container selections, tracked alongside the form. */
type LineSelection = {
  wasteStream: StreamPickerOption | null;
  container: ContainerPickerOption | null;
  /**
   * The line is linked, but the reader cannot see what it is linked to.
   *
   * `GET_SHIPPING_DOCUMENT_QUERY` selects both the ids and the resolved
   * records, and the field resolvers null the records for a facility the reader
   * cannot open while the form keeps the id. Rendered as an empty picker, a
   * permission boundary looks like missing data: a reader who "fills in the
   * blank" silently repoints the line.
   */
  streamOutOfView: boolean;
  containerOutOfView: boolean;
};

/** The label a picker shows for a link whose record the reader cannot open. */
const OUT_OF_VIEW_LABEL = "Linked outside your facilities";

/**
 * One line's waste stream and container pickers.
 *
 * The container list is filtered to the picked stream's own shippable
 * containers, and disabled until a stream is picked: a container belongs to
 * exactly one stream, so offering every container in the facility would let
 * someone pair a container with waste it does not hold.
 */
function LineLinkFields({
  facilityId,
  selection,
  onWasteStreamChange,
  onContainerChange,
}: {
  facilityId: string;
  selection: LineSelection;
  onWasteStreamChange: (stream: StreamPickerOption | null) => void;
  onContainerChange: (container: ContainerPickerOption | null) => void;
}) {
  const streamId = selection.wasteStream?.id;
  // A link the reader cannot open still shows as a link. Left blank it reads as
  // an unlinked line, and the container picker's `disabled={!streamId}` would
  // lock a row the reader cannot fill in either.
  const streamValue = selection.streamOutOfView
    ? { id: "", name: OUT_OF_VIEW_LABEL }
    : selection.wasteStream;
  const containerValue = selection.containerOutOfView
    ? { id: "", containerLabel: OUT_OF_VIEW_LABEL }
    : selection.container;
  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }}>
        <AsyncAutocomplete
          query={FACILITY_WASTE_STREAM_OPTIONS_QUERY}
          variables={{ facilityId }}
          getItems={(data) =>
            (data.facility?.wasteStreams?.items ?? []).map((stream) => ({
              id: stream.id,
              name: stream.name,
            }))
          }
          getOptionLabel={(stream) => stream.name}
          value={streamValue}
          onChange={onWasteStreamChange}
          lazyLoad
          fullWidth
          placeholderLabel="Waste stream"
          initialSortModel={[{ field: "name", sort: "asc" }]}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <AsyncAutocomplete
          query={SHIPPABLE_CONTAINERS_QUERY}
          variables={{ facilityId }}
          getItems={(data) =>
            (data.facility?.shippableContainers?.items ?? [])
              .filter((container) => container.wasteStream?.id === streamId)
              .map((container) => ({
                id: container.id,
                containerLabel: container.containerLabel,
              }))
          }
          getOptionLabel={(container) => container.containerLabel}
          value={containerValue}
          onChange={onContainerChange}
          disabled={!streamId && !selection.streamOutOfView}
          lazyLoad
          fullWidth
          placeholderLabel="Container"
          initialSortModel={[{ field: "containerLabel", sort: "asc" }]}
        />
      </Grid>
    </>
  );
}

/**
 * Wires one line's stream/container Autocompletes to the form and the
 * display-only `selections` state. A manual save writes whatever id (or
 * `null`) sits on the line, unmatched by the server, so there is nothing
 * beyond the id itself for these handlers to track.
 */
function useLineLinkHandlers(
  setValue: UseFormSetValue<ShippingDocumentFormValues>,
  setSelections: Dispatch<SetStateAction<LineSelection[]>>
) {
  const handleWasteStreamChange = (index: number, stream: StreamPickerOption | null) => {
    // A container belongs to exactly one stream, so a stream change strands
    // whatever container was picked for the old one. Clearing it here rather
    // than leaving the stale id is the only way the reviewer gets a fresh
    // container pick for the new stream.
    setSelections((prev) =>
      prev.map((sel, i) =>
        i === index
          ? {
              wasteStream: stream,
              container: null,
              streamOutOfView: false,
              containerOutOfView: false,
            }
          : sel
      )
    );
    setValue(`lines.${index}.wasteStreamId`, stream?.id ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(`lines.${index}.containerId`, null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const handleContainerChange = (index: number, container: ContainerPickerOption | null) => {
    setSelections((prev) =>
      prev.map((sel, i) => (i === index ? { ...sel, container, containerOutOfView: false } : sel))
    );
    setValue(`lines.${index}.containerId`, container?.id ?? null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  return { handleWasteStreamChange, handleContainerChange };
}

/** Kind/documentNumber/generator/transporter/destination/shipDate — the header half of the form. */
function HeaderFields({ control }: { control: FormControl }) {
  return (
    <Grid container spacing={2}>
      <Grid size={HALF}>
        <FormSelect
          name="kind"
          label="Kind"
          control={control}
          hideErrors
          selectItems={SHIPPING_DOCUMENT_KIND_OPTIONS.map((option) => ({
            value: option.value,
            display: option.label,
          }))}
        />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="documentNumber" label="Document number" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="generatorName" label="Generator" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="transporterName" label="Transporter" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="destinationName" label="Destination" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <DateField name="shipDate" label="Ship date" control={control} />
      </Grid>
    </Grid>
  );
}

/** One editable line row: description, quantity, unit, declared kind, codes, remove. */
function LineRow({
  control,
  getValues,
  facilityId,
  index,
  selection,
  onWasteStreamChange,
  onContainerChange,
  onRemove,
}: {
  control: FormControl;
  getValues: UseFormGetValues<ShippingDocumentFormValues>;
  facilityId: string;
  index: number;
  selection: LineSelection;
  onWasteStreamChange: (stream: StreamPickerOption | null) => void;
  onContainerChange: (container: ContainerPickerOption | null) => void;
  onRemove: () => void;
}) {
  return (
    /*
     * Top-aligned, not centred. A select is a different height from a text
     * field, so centring each cell against the tallest one in the row staggers
     * them into a staircase. Hanging them all from a common top edge keeps the
     * inputs level whatever they contain.
     *
     * The remove button sits outside the field grid so the six fields get the
     * full twelve columns; squeezing it in left the codes fields too narrow for
     * their own labels, which then wrapped inside the empty input.
     */
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Grid container spacing={1} sx={{ flex: 1 }}>
        <Grid size={LINE_DESCRIPTION}>
          <FormTextField
            name={`lines.${index}.description`}
            label="Description"
            control={control}
            fullWidth
          />
        </Grid>
        <Grid size={LINE_QUANTITY}>
          <FormTextField
            name={`lines.${index}.quantity`}
            label="Quantity"
            control={control}
            fullWidth
          />
        </Grid>
        <Grid size={LINE_UNIT}>
          <FormSelect
            name={`lines.${index}.unit`}
            label="Unit"
            control={control}
            hideErrors
            selectItems={UNIT_OPTIONS.map((option) => ({
              value: option.value,
              display: option.label,
            }))}
          />
        </Grid>
        <Grid size={LINE_THIRD}>
          <FormSelect
            name={`lines.${index}.declaredWasteKind`}
            label="Declared kind"
            control={control}
            hideErrors
            selectItems={DECLARED_WASTE_KIND_OPTIONS}
          />
        </Grid>
        <Grid size={LINE_THIRD}>
          <FormTextField
            name={`lines.${index}.federalWasteCodes`}
            label="Federal codes"
            control={control}
            fullWidth
          />
        </Grid>
        <Grid size={LINE_THIRD}>
          <FormTextField
            name={`lines.${index}.stateWasteCodes`}
            label="State codes"
            control={control}
            fullWidth
          />
        </Grid>
        <LineLinkFields
          facilityId={facilityId}
          selection={selection}
          onWasteStreamChange={onWasteStreamChange}
          onContainerChange={onContainerChange}
        />
      </Grid>
      <IconButton
        aria-label={`Remove line ${getValues(`lines.${index}.description`) || index + 1}`}
        onClick={onRemove}
        // Against the inputs rather than their labels, which sit above them.
        sx={{ mt: 1 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function LinesSection({
  control,
  getValues,
  facilityId,
  fields,
  selections,
  onWasteStreamChange,
  onContainerChange,
  onAppend,
  onRemove,
}: {
  control: FormControl;
  getValues: UseFormGetValues<ShippingDocumentFormValues>;
  facilityId: string;
  fields: { id: string }[];
  selections: LineSelection[];
  onWasteStreamChange: (index: number, stream: StreamPickerOption | null) => void;
  onContainerChange: (index: number, container: ContainerPickerOption | null) => void;
  onAppend: () => void;
  onRemove: (index: number) => void;
}) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Typography variant="subtitle2">Lines</Typography>
          {fields.length > 0 && (
            // Once, rather than under both code fields on every row, where it
            // repeated per line and made those two fields taller than the rest.
            <Typography variant="caption" color="text.secondary">
              Separate waste codes with commas.
            </Typography>
          )}
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={onAppend}>
          Add line
        </Button>
      </Stack>
      {fields.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No lines yet.
        </Typography>
      )}
      {fields.map((field, index) => (
        <LineRow
          key={field.id}
          control={control}
          getValues={getValues}
          facilityId={facilityId}
          index={index}
          selection={
            selections[index] ?? {
              wasteStream: null,
              container: null,
              streamOutOfView: false,
              containerOutOfView: false,
            }
          }
          onWasteStreamChange={(stream) => onWasteStreamChange(index, stream)}
          onContainerChange={(container) => onContainerChange(index, container)}
          onRemove={() => onRemove(index)}
        />
      ))}
    </Stack>
  );
}

export type EditShippingDocumentDialogProps = {
  open: boolean;
  onClose: () => void;
  document: Document;
  onSaved: () => void;
};

/**
 * Plain react-hook-form editor for a saved `WasteShippingDocument` — the
 * grid-based `ExtractionReviewGrid` is for AI-extraction review, not this.
 * Every field is optional, matching the mutation's own input shape; the
 * lines list is always sent in full on save (wholesale-replace semantics).
 */
export function EditShippingDocumentDialog({
  open,
  onClose,
  document,
  onSaved,
}: EditShippingDocumentDialogProps) {
  const alerts = useAlerts();
  const facilityId = document.facility?.id ?? "";
  const { control, handleSubmit, getValues, setValue } = useForm<ShippingDocumentFormValues>({
    defaultValues: toFormValues(document),
  });
  const { fields, append, remove } = useFieldArray({ control, name: "lines" });

  // Tracked in parallel with `fields`, indexed the same way, so each
  // Autocomplete can show the currently-picked stream/container's name without
  // the form itself (which only carries ids) having to look it up.
  // append/remove below keep the two arrays in lockstep.
  const [selections, setSelections] = useState<LineSelection[]>(() =>
    document.lines.map((line) => ({
      wasteStream: line.wasteStream ?? null,
      container: line.container ?? null,
      streamOutOfView: line.wasteStreamId != null && line.wasteStream == null,
      containerOutOfView: line.containerId != null && line.container == null,
    }))
  );

  const handleAppend = () => {
    append(EMPTY_LINE);
    setSelections((prev) => [
      ...prev,
      { wasteStream: null, container: null, streamOutOfView: false, containerOutOfView: false },
    ]);
  };
  const handleRemove = (index: number) => {
    remove(index);
    setSelections((prev) => prev.filter((_, i) => i !== index));
  };
  const { handleWasteStreamChange, handleContainerChange } = useLineLinkHandlers(
    setValue,
    setSelections
  );

  const [save, { loading: saving }] = useMutation(SAVE_WASTE_SHIPPING_DOCUMENT, {
    // The grid behind this dialog is the merged shipping-paperwork list, and
    // `documentNumber` / `shipDate` are its own item-level fields — normalizing
    // the saved WasteShippingDocument into the cache does not touch them, so
    // without this refetch an edited row keeps its old number and date until a
    // reload. GetShippingDocument is active under the dialog and carries the
    // rest of the document's fields.
    //
    // ShippingDocumentLines is the third, and it is the one a save invalidates
    // hardest: this mutation replaces every line rather than editing them in
    // place (see `WasteShippingDocumentInput.lines`), so every id the lines
    // grid is holding stops existing. Its `linesResult` is keyed by search,
    // page and sort, so normalization cannot reach it — without naming it here
    // the grid keeps rendering deleted rows until its arguments happen to
    // change.
    //
    // The shipment reads are the fourth group, and they are new here: this
    // dialog is now reachable from a shipment detail page, so a live
    // GetWasteShipment sits under it. `ShipmentReconciliation` has no `id`, so
    // Apollo cannot normalize it — editing a line quantity or the document
    // number leaves the reconciliation panel on a stale total and field diffs,
    // and the rail on a stale document number, until a reload. The shipment
    // lists carry the same reconciliation chip. Named individually rather than
    // through `SHIPMENT_REFETCH_QUERIES`, which also pulls container and
    // accumulation-area reads that a document edit cannot change.
    refetchQueries: [
      SHIPPING_PAPERWORK_QUERY,
      GET_SHIPPING_DOCUMENT_QUERY,
      SHIPPING_DOCUMENT_LINES_QUERY,
      "GetWasteShipment",
      "FacilityWasteShipments",
      "TenantWasteShipments",
    ],
  });

  const onSubmit = async (values: ShippingDocumentFormValues) => {
    try {
      await save({
        variables: {
          input: toSaveInput(values, { id: document.id, facilityId }),
        },
      });
      alerts.success("Shipping document saved");
      onSaved();
    } catch (error) {
      alerts.error(
        "Could not save the shipping document",
        error instanceof Error ? error.message : undefined
      );
    }
  };

  const sourceDocument = document.sourceDocument;

  return (
    /*
     * The same container the review dialog uses, so correcting a saved document
     * reads against the paper the way reviewing its reading does. Every field
     * here is a claim about that document, and checking one against the form
     * used to mean closing this dialog and opening the preview from the page
     * behind it.
     *
     * A document typed in by hand has no source file, and the column has to be
     * turned off explicitly: `showPreviewBoxIfFailed` defaults to true, so an
     * absent `previewUrl` alone still reserves half the dialog for an empty
     * box and squeezes the form into the other half.
     */
    <DocumentDialogContainer
      open={open}
      // A no-op rather than an absent handler while a save is in flight: this
      // container requires one, and the point is the same — a backdrop click
      // must not discard edits the mutation is still writing.
      onClose={saving ? noop : onClose}
      // The title-bar X renders only when `onClose` is truthy, so `noop` above
      // shows it and silently swallows the click. Hiding it while saving makes
      // the affordance match the behaviour. Cancel already greys itself out
      // through `primaryActionButtonProps.loading`.
      hideCloseButton={saving}
      maxWidth={sourceDocument?.downloadLink ? "xl" : "md"}
      fullWidth
      // Seven-five rather than an even split: this form carries a repeating
      // six-field line row, and the document is legible in the smaller share
      // where the fields are not.
      //
      // Only when there is a document to sit beside. `DocumentDialogContainer`
      // falls back to a full-width form through its own default, which an
      // explicit value overrides — so a hand-typed document rendered the form
      // in 7 of 12 columns with 5 dead ones.
      formColumnSize={sourceDocument?.downloadLink ? { xs: 12, md: 7 } : undefined}
      previewColumnSize={{ xs: 12, md: 5 }}
      title="Edit shipping document"
      previewUrl={sourceDocument?.downloadLink ?? undefined}
      fileExtension={sourceDocument?.fileExtension ?? undefined}
      showPreviewBoxIfFailed={Boolean(sourceDocument?.downloadLink)}
      primaryActionButtonProps={{ loading: saving, onClick: handleSubmit(onSubmit) }}
      // Tall enough for a page of a bill of lading to be legible beside the
      // form, matching the review dialog. Only when there is one to show.
      sx={
        sourceDocument?.downloadLink
          ? { "& .MuiDialog-paper": { height: "calc(100vh - 80px)", maxHeight: "1100px" } }
          : undefined
      }
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <HeaderFields control={control} />
        <Divider />
        <LinesSection
          control={control}
          getValues={getValues}
          facilityId={facilityId}
          fields={fields}
          selections={selections}
          onWasteStreamChange={handleWasteStreamChange}
          onContainerChange={handleContainerChange}
          onAppend={handleAppend}
          onRemove={handleRemove}
        />
      </Stack>
    </DocumentDialogContainer>
  );
}
